import moment from "moment";
import { IDataProvider } from "../Data/IDataProvider";
import { IGoal } from "../Entity/IGoal";
import {
    IGoalDataPoint,
    maxDatapointAgeForDaysLeft,
    resourceNameGoalDataPoint,
} from "../Entity/IGoalDataPoint";
import { EGoalDatapointType } from "../Goals/GoalDataPoints/EGoalDatapointType";
import { IsDatapointRelevant } from "../Goals/GoalDataPoints/IsDatapointRelevant";
import { EncodeTimeZone } from "../Services/TimeServices";
import { goalsService } from "../Services/GoalsService";

export async function GetDatapointsForGoalsContainer(
    dataProvider: IDataProvider
) {
    const oldestDataPointTime = EncodeTimeZone(
        moment().subtract(maxDatapointAgeForDaysLeft, "days").format()
    );

    return dataProvider.GetRecordsAsync<IGoalDataPoint>(
        `${resourceNameGoalDataPoint}?lastmodifiedtime=gte.${oldestDataPointTime}`
    );
}

export function FilterDatapointsForGoal(
    goal: IGoal,
    datapoints: IGoalDataPoint[],
    type: EGoalDatapointType
): IGoalDataPoint[] {
    const result = datapoints.filter(
        (dp) => dp.goalid === goal.id && dp.egoaldatapointtype === type
    );

    return result;
}

export async function LoadDatapointsAsync(
    goal: IGoal,
    type: EGoalDatapointType,
    dataProvider: IDataProvider
) {
    const goalDatapoints = await dataProvider.GetRecordsAsync<IGoalDataPoint>(
        `${resourceNameGoalDataPoint}?goalid=eq.${goal.id}&egoaldatapointtype=eq.${type}&order=id.desc`
    );

    return goalDatapoints;
}

export async function AddGoalDatapointFromLogEntryAsync(
    entryText: string,
    dataProvider: IDataProvider
) {
    const matchingGoal = await goalsService.GetMatchingGoalAsync(
        entryText,
        dataProvider
    );

    let goalDatapointId: number | undefined;

    if (matchingGoal) {
        goalDatapointId = await AddGoalDatapointAsync(
            matchingGoal,
            entryText,
            dataProvider
        );
    }

    return goalDatapointId;
}

async function AddGoalDatapointAsync(
    matchingGoal: IGoal,
    entryText: string,
    dataProvider: IDataProvider
) {
    const entryWords = entryText.split(" ");
    const goalValue = +entryWords[3]; // The plus sign casts it the word as a number, and returns NaN if it fails
    const goalComment = entryWords.splice(4).join(" ") || "-";

    if (!isNaN(goalValue)) {
        const datapoint: Partial<IGoalDataPoint> = {
            goalid: matchingGoal.id,
            value: goalValue,
            comment: goalComment,
            egoaldatapointtype: EGoalDatapointType.GoalDatapoint,
            lastmodifiedtime: moment().format(),
        };

        await dataProvider.AddRecordAsync(resourceNameGoalDataPoint, datapoint);

        // AddStatusMessage(
        // 	`Posted ${goalValue} points to the goal ${matchingGoal.name}`,
        // 	EStatusMessageType.success
        // );

        const newDataPoint = await dataProvider.GetRecordsAsync<IGoalDataPoint>(
            `${resourceNameGoalDataPoint}?order=id.desc&limit=1`
        );

        const newDataPointId = newDataPoint[0].id;
        return newDataPointId;
    }
}

export async function GetLatestGoalDatapointTimeAsync(
    goal: IGoal,
    dataProvider: IDataProvider
) {
    const datapoints = await LoadDatapointsAsync(
        goal,
        EGoalDatapointType.GoalDatapoint,
        dataProvider
    );

    if (datapoints && datapoints.length) {
        const relevantDatapoints = datapoints.filter((dp) =>
            IsDatapointRelevant(dp, goal.startdate)
        );

        if (relevantDatapoints.length > 0) {
            return relevantDatapoints[0].lastmodifiedtime;
        }
    }

    return undefined;
}

export async function EnrichWithDatapoints(
    incoming: IGoal,
    merged: IGoalWithDatapoints,
    dataProvider: IDataProvider
) {
    const datapoints = await LoadDatapointsAsync(
        incoming,
        EGoalDatapointType.GoalDatapoint,
        dataProvider
    );
    if (!datapoints) {
        merged.datapoints = [];
    } else {
        merged.datapoints = datapoints;
    }
}

export interface IGoalWithDatapoints extends IGoal {
    datapoints: IGoalDataPoint[];
}

export async function DeleteDatapointAsync(
    goalName: string,
    datapointId: number,
    dataProvider: IDataProvider
) {
    await dataProvider.DeleteRecordsAsync(
        `${resourceNameGoalDataPoint}?id=eq.${datapointId}`
    );

    // TODO: This should be on the client side
    // await AddStatusMessage(
    // 	`Deleted datapoint ${datapointId} from the goal ${goalName}`,
    // 	EStatusMessageType.success
    // );
}
